/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Version, VersionDownloadKind } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { formatDuration, formatTokens, startDownload } from '../services/download';

import { MoreIcon, PackageIcon } from './icons';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const statusColor = (v: Version) =>
  v.status === 'build_failed' ? 'error' : v.status === 'built' ? 'success' : 'default';

const deploymentColor = (state: NonNullable<Version['deployment_state']>) =>
  state === 'applied' ? 'success' : state === 'failed' || state === 'rolled_back' ? 'error' : 'default';

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="div">
    <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>
      {label}
    </Box>
    {children}
  </Typography>
);

/** A build is called by its version once it has one; until then, and when it failed, by its build number. */
export const titleOf = (v: Version): string => (v.version === null ? `Build ${v.build}` : `Version ${v.version}`);

export interface GeneratedAppsProps {
  projectId: string;
  versions: Version[];
  devices: Device[];
  onDeployAgain: (build: number) => void;
  /** The build shown, when the page chooses it (from the roadmap); undefined shows the newest. */
  chosen?: number;
  onChoose?: (build: number | undefined) => void;
}

/** One line per build in the chooser: its name and where it got to. */
const optionLabel = (v: Version): string =>
  [titleOf(v), (v.deployment_state ?? v.status).replace('_', ' ')].join(' · ');

/**
 * The App card: a chooser of the app's builds and, for the one chosen, where it has got to, what it changed and its
 * downloads. What is needed less often (send again, the log, sizes and times) is behind the menu. It shows the newest
 * build until another is chosen, and goes back to the newest whenever a new build appears.
 */
export const GeneratedApps = ({
  projectId,
  versions,
  devices,
  onDeployAgain,
  chosen: given,
  onChoose,
}: GeneratedAppsProps) => {
  const { api } = useAuth();
  const [own, setOwn] = useState<number>();
  const chosen = onChoose ? given : own;
  const setChosen = onChoose ?? setOwn;
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [details, setDetails] = useState(false);
  const [preparing, setPreparing] = useState<string>();
  const [error, setError] = useState<string>();

  const newest = versions[0]?.build;
  useEffect(() => {
    if (!onChoose) {
      setOwn(undefined);
    }
  }, [newest, onChoose]);

  const v = versions.find((b) => b.build === chosen) ?? versions[0];

  const download = async (build: number, kind: VersionDownloadKind) => {
    setPreparing(`${build}-${kind}`);
    setError(undefined);
    try {
      const link = await api.getVersionDownload(projectId, build, kind);
      startDownload(link.url, link.filename);
    } catch (caught) {
      setError(describe(caught, 'Could not prepare the download.'));
    } finally {
      setPreparing(undefined);
    }
  };

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="App"
      sx={{ p: 2, flexShrink: 0, minHeight: 228, display: 'flex', flexDirection: 'column' }}
    >
      <Stack spacing={1.5} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <PackageIcon sx={{ fontSize: 20, color: v?.status === 'built' ? 'primary.main' : 'text.disabled' }} />
          <Typography variant="subtitle1" sx={{ flexShrink: 0 }}>
            App
          </Typography>
          {v && (
            <TextField
              select
              size="small"
              label="Version"
              value={v.build}
              onChange={(event) => {
                const build = Number(event.target.value);
                setChosen(build === newest ? undefined : build);
              }}
              // The browser's own list, as for the runtime: the builds are polled, and a redraw must not close it.
              slotProps={{
                select: { native: true },
                inputLabel: { shrink: true },
                htmlInput: { sx: { py: 0.5, typography: 'body2' } },
              }}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {versions.map((b) => (
                <option key={b.build} value={b.build}>
                  {optionLabel(b)}
                </option>
              ))}
            </TextField>
          )}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {!v ? (
          <Typography variant="body2" color="text.secondary">
            No builds yet.
          </Typography>
        ) : (
          <Stack spacing={1.25} role="group" aria-label={titleOf(v)} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
              <Chip size="small" label={v.status.replace('_', ' ')} color={statusColor(v)} />
              {v.deployment_state && (
                <Chip
                  size="small"
                  label={v.deployment_state.replace('_', ' ')}
                  color={deploymentColor(v.deployment_state)}
                />
              )}
              {v.build !== newest && (
                <Button size="small" onClick={() => setChosen(undefined)} sx={{ ml: 'auto', minWidth: 0 }}>
                  Latest
                </Button>
              )}
            </Stack>
            {v.summary && (
              <Tooltip title={v.summary} placement="right">
                <Typography
                  variant="body2"
                  sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {v.summary}
                </Typography>
              </Tooltip>
            )}
            {v.error && (
              <Typography
                variant="body2"
                color="error.light"
                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {v.error}
              </Typography>
            )}
            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', mt: 'auto !important' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={v.status !== 'built' || preparing !== undefined}
                onClick={() => void download(v.build, 'binary')}
              >
                {preparing === `${v.build}-binary` ? 'Preparing...' : 'Binary (.zip)'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={preparing !== undefined}
                onClick={() => void download(v.build, 'source')}
              >
                {preparing === `${v.build}-source` ? 'Preparing...' : 'Source (.zip)'}
              </Button>
              <IconButton
                size="small"
                aria-label="More about this version"
                aria-haspopup="menu"
                onClick={(event) => setMenu(event.currentTarget)}
                sx={{ ml: 'auto' }}
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
              {v.status === 'built' && (
                <MenuItem
                  onClick={() => {
                    setMenu(null);
                    onDeployAgain(v.build);
                  }}
                >
                  Deploy again
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setMenu(null);
                  setDetails(true);
                }}
              >
                {v.log_tail ? 'Details and build log' : 'Details'}
              </MenuItem>
            </Menu>
            <Dialog open={details} onClose={() => setDetails(false)} maxWidth="md" fullWidth>
              <DialogTitle>{titleOf(v)}</DialogTitle>
              <DialogContent>
                <Stack spacing={1.5}>
                  {v.summary && <Typography variant="body2">{v.summary}</Typography>}
                  {v.error && (
                    <Typography variant="body2" color="error.light">
                      {v.error}
                    </Typography>
                  )}
                  <Box>
                    <Fact label="Device">{devices.find((d) => d.device_id === v.device_id)?.name ?? 'unknown'}</Fact>
                    <Fact label="Created">{new Date(v.created_at).toLocaleString()}</Fact>
                    {v.artifact_size ? <Fact label="Size">{`${(v.artifact_size / 1e6).toFixed(1)} MB`}</Fact> : null}
                    {v.elapsed_ms !== null && <Fact label="Time">{formatDuration(v.elapsed_ms)}</Fact>}
                    {v.input_tokens !== null && v.output_tokens !== null && (
                      <Fact label="Tokens">{`${formatTokens(v.input_tokens)} in / ${formatTokens(v.output_tokens)} out`}</Fact>
                    )}
                  </Box>
                  {v.log_tail && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Build log
                      </Typography>
                      <Typography
                        component="pre"
                        variant="caption"
                        sx={{
                          m: 0,
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          overflowWrap: 'anywhere',
                          maxHeight: 420,
                          overflowY: 'auto',
                        }}
                      >
                        {v.log_tail}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDetails(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
